// Projectable "Pantalla Gigante" for an A Go Go tournament venue.
// Public (unauth), auto-refreshing every 15s: Top 10 standings + Calcuta +
// Salidas (tee times publish live as the raffle happens). Route:
// /:slug/tournaments/:tournamentId/pantalla
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/App";

const POLL_MS = 15000;

function toPar(v) {
  if (v == null) return "—";
  if (v === 0) return "E";
  return v > 0 ? `+${v}` : String(v);
}

export default function TournamentBigScreenPage() {
  const { slug, tournamentId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [clock, setClock] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/clubs/${slug}/tournaments/${tournamentId}/agogo`);
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch { setErr(true); }
  }, [slug, tournamentId]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [load]);

  if (err) return <Shell><div style={{ textAlign: "center", opacity: 0.7, fontSize: 28 }}>Torneo no encontrado</div></Shell>;
  if (!data) return <Shell><div style={{ textAlign: "center", opacity: 0.7, fontSize: 28 }}>Cargando…</div></Shell>;

  const lb = data.leaderboard;
  const rows = (lb?.rows || []).slice(0, 10);
  const cal = data.calcutta || {};
  const money = (v) => {
    try { return new Intl.NumberFormat("es-MX", { style: "currency", currency: cal.currency || "MXN", maximumFractionDigits: 0 }).format(v || 0); }
    catch { return `${v} ${cal.currency || ""}`; }
  };
  const starts = data.starts || {};
  const roundNums = Object.keys(starts).map(Number).sort();
  const activeStarts = roundNums.map((rn) => starts[String(rn)]).find((s) => (s?.holes || []).length) || starts["1"];
  const activeRoundNum = roundNums.find((rn) => (starts[String(rn)]?.holes || []).length) || 1;

  return (
    <Shell>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.kicker}>A GO GO POR EQUIPOS</div>
          <div style={S.title}>{data.tournament?.name}</div>
          <div style={S.sub}>Par {data.config?.par_total} · Corte {data.config?.cut_teams} · {STATUS[data.tournament?.status] || data.tournament?.status}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={S.livePill}><span style={S.dot} /> EN VIVO</div>
          <div style={S.clock}>{clock.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
        </div>
      </div>

      <div style={S.grid}>
        {/* LEFT: Top 10 */}
        <div style={S.panel}>
          <div style={S.panelHead}>CLASIFICACIÓN · TOP 10</div>
          <div style={S.lbHeadRow}>
            <span style={{ width: 54 }}>POS</span>
            <span style={{ flex: 1 }}>EQUIPO</span>
            <span style={S.numCol}>R1</span>
            <span style={S.numCol}>R2</span>
            <span style={S.numCol}>TOT</span>
            <span style={S.numCol}>PAR</span>
          </div>
          {rows.length === 0 ? <div style={S.empty}>Sin tarjetas aún</div> : rows.map((r, i) => (
            <div key={r.team_id}>
              {i === lb.cut_teams && rows.length > lb.cut_teams ? <div style={S.cutLine}>LÍNEA DE CORTE · PASAN {lb.cut_teams}</div> : null}
              <div style={{ ...S.lbRow, ...(i % 2 ? S.lbRowAlt : {}), ...(i === 0 ? S.lbRowLead : {}) }}>
                <span style={{ ...S.pos, ...(i < 3 ? S.posTop : {}) }}>{r.position}</span>
                <span style={S.teamName}>{r.name}</span>
                <span style={S.numCol}>{r.r1_complete ? r.r1_total : "·"}</span>
                <span style={S.numCol}>{r.r2_complete ? r.r2_total : (r.r2_total || "—")}</span>
                <span style={{ ...S.numCol, ...S.tot }}>{r.combined_total}</span>
                <span style={{ ...S.numCol, ...S.par }}>{toPar(r.to_par)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: Calcuta + Salidas */}
        <div style={S.rightCol}>
          {cal.enabled ? (
            <div style={S.panel}>
              <div style={S.panelHead}>CALCUTA</div>
              <div style={S.poolRow}>
                <div style={S.poolCard}><div style={S.poolLbl}>BOLSA TOTAL</div><div style={S.poolVal}>{money(cal.total_raised)}</div></div>
                <div style={S.poolCard}><div style={S.poolLbl}>BOLSA NETA</div><div style={S.poolVal}>{money(cal.net_pool)}</div></div>
              </div>
              {(cal.payouts || []).map((p) => (
                <div key={p.position} style={S.payRow}>
                  <span style={S.payPos}>{p.position}º</span>
                  <span style={{ flex: 1, fontWeight: 700 }}>{p.team_name || "Por definir"}</span>
                  <span style={S.payAmt}>{money(p.amount)}</span>
                </div>
              ))}
            </div>
          ) : null}

          {cal.enabled && cal.teams_sold?.some((t) => t.starting_price != null) ? (
            <div style={S.panel}>
              <div style={S.panelHead}>PRECIO DE SALIDA</div>
              <div style={S.spGrid}>
                {cal.teams_sold.filter((t) => t.starting_price != null).sort((a, b) => (a.auction_order || 0) - (b.auction_order || 0)).map((t) => (
                  <div key={t.team_id} style={S.spRow}>
                    <span style={S.teamName}>{t.auction_order ? `${t.auction_order}. ` : ""}{t.name}</span>
                    <span style={S.payAmt}>{money(t.starting_price)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={S.panel}>
            <div style={S.panelHead}>SALIDAS · RONDA {activeRoundNum}{activeStarts?.time ? ` · ${activeStarts.time}` : ""}</div>
            {(activeStarts?.holes || []).length === 0 ? <div style={S.empty}>Esperando la rifa de salidas…</div> : (
              <div style={S.startsGrid}>
                {activeStarts.holes.map((h) => (
                  <div key={h.hole} style={S.startHole}>
                    <div style={S.startHoleNum}>H{h.hole}</div>
                    {h.teams.map((tm) => (
                      <div key={tm.team_id} style={S.startTeam}><b style={{ color: "#E8C76A" }}>{tm.slot}</b> {tm.name}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

const STATUS = { draft: "Próximo", open: "Inscripción abierta", in_progress: "En curso", completed: "Finalizado" };

function Shell({ children }) {
  return <div style={S.shell}>{children}<style>{`@keyframes blk{0%,100%{opacity:1}50%{opacity:.2}}`}</style></div>;
}

const S = {
  shell: { minHeight: "100vh", background: "#0B1D33", color: "#F4F1EC", fontFamily: "system-ui, sans-serif", padding: "28px 36px", boxSizing: "border-box" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid rgba(232,199,106,0.3)", paddingBottom: 18, marginBottom: 20 },
  kicker: { color: "#E8C76A", fontSize: 16, fontWeight: 800, letterSpacing: 3 },
  title: { fontSize: 46, fontWeight: 900, lineHeight: 1.05, marginTop: 4 },
  sub: { fontSize: 20, opacity: 0.7, marginTop: 6 },
  livePill: { display: "inline-flex", alignItems: "center", gap: 10, background: "rgba(197,48,48,0.2)", color: "#F4A4A4", padding: "8px 16px", borderRadius: 999, fontSize: 18, fontWeight: 800, letterSpacing: 2 },
  dot: { width: 12, height: 12, borderRadius: 8, background: "#F26D6D", animation: "blk 1.2s infinite" },
  clock: { fontSize: 34, fontWeight: 800, marginTop: 10, fontVariantNumeric: "tabular-nums" },
  grid: { display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24 },
  rightCol: { display: "flex", flexDirection: "column", gap: 24 },
  panel: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 22 },
  panelHead: { color: "#E8C76A", fontSize: 20, fontWeight: 800, letterSpacing: 2, marginBottom: 16 },
  lbHeadRow: { display: "flex", alignItems: "center", fontSize: 15, opacity: 0.5, fontWeight: 700, letterSpacing: 1, paddingBottom: 8 },
  numCol: { width: 72, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  lbRow: { display: "flex", alignItems: "center", fontSize: 26, fontWeight: 700, padding: "11px 8px", borderRadius: 10 },
  lbRowAlt: { background: "rgba(255,255,255,0.03)" },
  lbRowLead: { background: "rgba(232,199,106,0.14)" },
  pos: { width: 54, fontWeight: 900, opacity: 0.8 },
  posTop: { color: "#E8C76A", opacity: 1 },
  teamName: { flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: 10 },
  tot: { fontWeight: 900 },
  par: { color: "#E8C76A", fontWeight: 800 },
  cutLine: { textAlign: "center", color: "#F4A4A4", fontSize: 14, fontWeight: 800, letterSpacing: 2, padding: "6px 0", borderTop: "2px dashed rgba(244,164,164,0.5)", borderBottom: "2px dashed rgba(244,164,164,0.5)", margin: "6px 0" },
  empty: { opacity: 0.5, fontSize: 20, textAlign: "center", padding: 20 },
  poolRow: { display: "flex", gap: 14, marginBottom: 16 },
  poolCard: { flex: 1, background: "#09182A", borderRadius: 12, padding: "14px 16px", textAlign: "center", border: "1px solid rgba(232,199,106,0.25)" },
  poolLbl: { color: "#E8C76A", fontSize: 13, fontWeight: 800, letterSpacing: 1 },
  poolVal: { fontSize: 30, fontWeight: 900, marginTop: 4 },
  payRow: { display: "flex", alignItems: "center", gap: 12, fontSize: 22, padding: "9px 0", borderTop: "1px solid rgba(255,255,255,0.08)" },
  payPos: { width: 44, fontWeight: 900, color: "#E8C76A" },
  payAmt: { fontWeight: 900, color: "#8FD6A0" },
  spGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 20px" },
  spRow: { display: "flex", alignItems: "center", gap: 10, fontSize: 18, padding: "5px 0", borderTop: "1px solid rgba(255,255,255,0.06)" },
  startsGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 },
  startHole: { background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: "8px 10px" },
  startHoleNum: { color: "#E8C76A", fontWeight: 900, fontSize: 18, marginBottom: 4 },
  startTeam: { fontSize: 15, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
};
